import React, {useState} from 'react';
import Modal from 'react-bootstrap/Modal';
import {Form, FormSelect} from 'react-bootstrap';
import ButtonSubmit from '../../modules/form/ButtonSubmit';
import {useIntl} from 'react-intl';
import {useClientId} from '../../hooks/useClientId';
import {handleEmailChange} from '../../api/email';
import {Customer} from '../../../Domains/Auth/Types/customer';
import {User} from '../../../Domains/Auth/Types/User';
import {useCreateUser} from '../../../Domains/Auth/Hooks/UserManager';
import Role from '../../classes/api/Role';
import RoleSwitchList from './RoleSwitchList';

interface ModalAddUserProps {
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  customer: Customer;
  users: User[];
  roles: Role[];
}

const ModalAddUser = ({
  showModal,
  setShowModal,
  customer,
  users,
  roles,
}: ModalAddUserProps): JSX.Element => {
  const intl = useIntl();

  const [useList, setUseList] = useState(1);
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [firstname, setFirstname] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [emailError, setEmailError] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
  const [rolesError, setRolesError] = useState(false);
  const clientId = useClientId();

  const resetForm = () => {
    setName('');
    setFirstname('');
    setEmail('');
    setEmailError('');
    setUseList(1);
    setSelectedUser('');
    setSelectedRoles([]);
    setRolesError(false);
  };

  const {mutate, isLoading} = useCreateUser();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (emailError) {
      return;
    }
    // sans rôle, le contact n'apparaît pas sur la BU s'il est déjà rattaché à une autre
    if (selectedRoles.length === 0) {
      setRolesError(true);
      return;
    }

    if (useList === 1) {
      if (selectedUser === '') {
        alert(intl.formatMessage({id: 'USER_MANAGER.FORM.SELECT_USER_ALERT'}));
      } else {
        const user = users.find((user) => user.mail === selectedUser);

        if (user) {
          mutate({
            name: user.name,
            first_name: user.first_name,
            mail: user.mail,
            entity_id: customer.id,
            system: customer.system,
            roles: selectedRoles,
          });
        }
      }
    } else {
      mutate({
        name: name,
        first_name: firstname,
        mail: email,
        entity_id: customer.id,
        system: customer.system,
        roles: selectedRoles,
      });
    }
    setShowModal(false);

    resetForm();
  };

  return (
    <Modal
      show={showModal}
      onHide={() => {
        setShowModal(false);
        resetForm();
      }}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {intl.formatMessage({
            id: 'SETTINGS.ADD.USER',
          })}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group controlId="formUseList">
            <Form.Label>{intl.formatMessage({id: 'USER_MANAGER.FORM.CREATION_TYPE'})}</Form.Label>
            <FormSelect
              value={useList}
              onChange={(event) => setUseList(parseInt(event.target.value))}
            >
              <option value={1} key={1}>
                {intl.formatMessage({id: 'USER_MANAGER.FORM.USE_LIST'})}
              </option>
              <option value={0} key={0}>
                {intl.formatMessage({id: 'USER_MANAGER.FORM.NEW_USER'})}
              </option>
            </FormSelect>
          </Form.Group>
          {useList === 1 && (
            <>
              <Form.Group controlId="usersList">
                <Form.Label>{intl.formatMessage({id: 'USER_MANAGER.FORM.USERS_LIST'})}</Form.Label>
                <FormSelect
                  value={selectedUser}
                  onChange={(event) => setSelectedUser(event.target.value)}
                >
                  <option value="" key="none@none.fr">
                    {intl.formatMessage({id: 'USER_MANAGER.FORM.SELECT_USER_PLACEHOLDER'})}
                  </option>
                  {users.map((user) => (
                    <option value={user.mail} key={user.mail}>
                      {user.first_name} {user.name} ({user.mail})
                    </option>
                  ))}
                </FormSelect>
              </Form.Group>
            </>
          )}
          {useList === 0 && (
            <>
              <Form.Group controlId="formName">
                <Form.Label>{intl.formatMessage({id: 'USER_MANAGER.FORM.NAME'})}</Form.Label>
                <Form.Control
                  type="text"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  required
                />
              </Form.Group>
              <Form.Group controlId="formSurname">
                <Form.Label>{intl.formatMessage({id: 'USER_MANAGER.FORM.FIRST_NAME'})}</Form.Label>
                <Form.Control
                  type="text"
                  value={firstname}
                  onChange={(event) => setFirstname(event.target.value)}
                  required
                />
              </Form.Group>
              <Form.Group controlId="formEmail">
                <Form.Label>{intl.formatMessage({id: 'USER_MANAGER.FORM.EMAIL'})}</Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={(event) =>
                    handleEmailChange(
                      event as React.ChangeEvent<HTMLInputElement>,
                      setEmail,
                      clientId,
                      setEmailError
                    )
                  }
                  required
                />
                {emailError && <div className="text-danger">{emailError}</div>}{' '}
              </Form.Group>
            </>
          )}
          <Form.Label className="mt-4">
            {intl.formatMessage({id: 'USER_MANAGER.RIGHTS.ASSIGNED_ROLES'})}
          </Form.Label>
          <RoleSwitchList
            roles={roles}
            selectedRoles={selectedRoles}
            onChange={(roleIds) => {
              setSelectedRoles(roleIds);
              setRolesError(false);
            }}
            idPrefix="add-role"
          />
          {rolesError && (
            <div className="text-danger">
              {intl.formatMessage({id: 'USER_MANAGER.FORM.SELECT_ROLE_ALERT'})}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <ButtonSubmit type="submit" textId="SAVE" disabled={false} isLoading={isLoading} />
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ModalAddUser;
